import axios from "axios";
import { AuthService } from "../services/AuthService";
import { API_BASE_URL, normalizeApiBaseUrl, toApiBaseUrlV2, } from "../constants";
class MedosClient {
    static getExternalMemberId() {
        return this.externalMemberId;
    }
    static getDoctorInfo() {
        return this.doctorInfo;
    }
    static getDoctorInfoByDoctorId(doctorId) {
        return this.doctorInfoByDoctorId[String(doctorId)] || null;
    }
    static hasManualDoctorConfig() {
        if (this.doctorInfo && Object.keys(this.doctorInfo).length > 0) {
            return true;
        }
        return Object.keys(this.doctorInfoByDoctorId).length > 0;
    }
    static async init({ apiKey, externalMemberId, doctorInfo, doctorInfoByDoctorId, baseUrl, iss, }) {
        if (!apiKey) {
            throw new Error("MedosClient.init() requires 'apiKey'");
        }
        if (this.initPromise) {
            return this.initPromise;
        }
        this.initPromise = (async () => {
            try {
                const resolvedBaseUrl = normalizeApiBaseUrl(baseUrl || API_BASE_URL);
                const sessionToken = await AuthService.init(apiKey, resolvedBaseUrl, {
                    iss,
                });
                this.apiKey = apiKey;
                this.externalMemberId = externalMemberId || null;
                this.doctorInfo = doctorInfo || null;
                this.doctorInfoByDoctorId = doctorInfoByDoctorId || {};
                this.initializeAxiosInstance(sessionToken, resolvedBaseUrl);
            }
            catch (e) {
                this.initPromise = null;
                throw new Error(`MedosClient.init failed: ${e.message}`);
            }
        })();
        return this.initPromise;
    }
    static async initWithSession({ sessionToken, baseUrl, }) {
        if (!sessionToken) {
            throw new Error("MedosClient.initWithSession() requires 'sessionToken'");
        }
        if (this.initPromise) {
            return this.initPromise;
        }
        this.initPromise = (async () => {
            try {
                this.initializeAxiosInstance(sessionToken, normalizeApiBaseUrl(baseUrl || API_BASE_URL));
            }
            catch (e) {
                this.initPromise = null;
                throw new Error(`MedosClient.initWithSession failed: ${e.message}`);
            }
        })();
        return this.initPromise;
    }
    static initializeAxiosInstance(sessionToken, baseURL) {
        this.token = sessionToken;
        this.baseURL = normalizeApiBaseUrl(baseURL);
        this.instance = axios.create({
            baseURL: this.baseURL,
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${sessionToken}`,
            },
        });
        this.instance.interceptors.request.use((config) => {
            if (!config.headers)
                config.headers = {};
            const headersAny = config.headers;
            const isFormData = config.data instanceof FormData ||
                (config.data &&
                    typeof config.data === "object" &&
                    config.data.constructor &&
                    config.data.constructor.name === "FormData");
            if (isFormData) {
                delete headersAny["Content-Type"];
                if (config.headers.common) {
                    delete config.headers.common["Content-Type"];
                }
            }
            if (this.token) {
                headersAny["Authorization"] = `Bearer ${this.token}`;
            }
            return config;
        });
        this.instance.interceptors.response.use((response) => response, async (error) => {
            const originalRequest = error.config;
            if (error.response?.status === 401 &&
                !originalRequest._retry &&
                this.refreshHandler) {
                originalRequest._retry = true;
                if (this.isRefreshing) {
                    return new Promise((resolve, reject) => {
                        this.pendingRequests.push((token) => {
                            if (token && originalRequest.headers) {
                                originalRequest.headers["Authorization"] =
                                    `Bearer ${token}`;
                            }
                            resolve(this.instance.request(originalRequest));
                        });
                    });
                }
                this.isRefreshing = true;
                try {
                    const newToken = await this.refreshHandler();
                    this.isRefreshing = false;
                    if (newToken) {
                        this.setToken(newToken);
                    }
                    this.pendingRequests.forEach((cb) => cb(newToken));
                    this.pendingRequests = [];
                    if (newToken && originalRequest.headers) {
                        originalRequest.headers["Authorization"] =
                            `Bearer ${newToken}`;
                    }
                    return this.instance.request(originalRequest);
                }
                catch (refreshErr) {
                    this.isRefreshing = false;
                    this.pendingRequests.forEach((cb) => cb(null));
                    this.pendingRequests = [];
                    return Promise.reject(refreshErr);
                }
            }
            if (error.response?.status === 429 && this.instance) {
                const rlRequest = originalRequest;
                const attempt = rlRequest._rlRetry ?? 0;
                const MAX_RETRIES = 2;
                if (attempt < MAX_RETRIES) {
                    rlRequest._rlRetry = attempt + 1;
                    const retryAfterSec = Number(error.response.headers?.["retry-after"]);
                    const waitMs = Number.isFinite(retryAfterSec) && retryAfterSec > 0
                        ? Math.min(retryAfterSec * 1000, 3000)
                        : 300 * 2 ** attempt;
                    await new Promise((resolve) => setTimeout(resolve, waitMs));
                    return this.instance.request(rlRequest);
                }
            }
            return Promise.reject(error);
        });
    }
    static async fetchTheme() {
        if (!this.instance) {
            throw new Error("MedosClient not initialized. Call MedosClient.init() first.");
        }
        const { WorkspaceService } = await import("../services/WorkspaceService");
        return WorkspaceService.fetchTheme();
    }
    static get client() {
        if (!this.instance && !this.initPromise) {
            throw new Error("MedosClient not initialized. Call MedosClient.init() or MedosClient.initWithSession() first.");
        }
        if (!this.instance && this.initPromise) {
            throw new Error("MedosClient initialization is in progress. Please wait for initialization to complete before accessing the client.");
        }
        return this.instance;
    }
    static async ensureInitialized() {
        if (this.instance) {
            return this.instance;
        }
        if (this.initPromise) {
            await this.initPromise;
            if (this.instance) {
                return this.instance;
            }
        }
        throw new Error("MedosClient not initialized. Call MedosClient.init() or MedosClient.initWithSession() first.");
    }
    static isInitialized() {
        return this.instance !== null;
    }
    static getApiBaseUrl() {
        return this.baseURL;
    }
    static getApiBaseUrlV2() {
        return toApiBaseUrlV2(this.baseURL);
    }
    static setToken(token) {
        this.token = token;
        if (this.instance) {
            const defaults = this.instance.defaults;
            if (!defaults.headers)
                defaults.headers = {};
            if (!defaults.headers.common)
                defaults.headers.common = {};
            defaults.headers.common["Authorization"] = `Bearer ${token}`;
        }
    }
    static setRefreshHandler(handler) {
        this.refreshHandler = handler;
    }
}
MedosClient.instance = null;
MedosClient.token = null;
MedosClient.refreshHandler = null;
MedosClient.isRefreshing = false;
MedosClient.pendingRequests = [];
MedosClient.initPromise = null;
MedosClient.apiKey = null;
MedosClient.baseURL = API_BASE_URL;
MedosClient.externalMemberId = null;
MedosClient.doctorInfo = null;
MedosClient.doctorInfoByDoctorId = {};
export { MedosClient };
